import { MouseEvent, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';

import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import IconUpload from 'approot/shared/icons/icon-upload';
import { getAcceptExtensions, getOptionalProps } from './file-dropper.utils';
import { Props } from './file-dropper-image.constants';
import * as FileDropperStyled from './file-dropper.style';
import * as FileDropperImageStyled from './file-dropper-image.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { useFileDropperContext } from './file-dropper.context';
import { FileDropperProgress } from './file-dropper';
import { useFileDropperHandlers } from './file-dropper.hooks';
import { PStyled } from 'approot/shared/styles/typography.style';
import { spacing4 } from 'approot/shared/styles/spacing.style';

const Styled = {
  ...FileDropperStyled,
  ...FileDropperImageStyled,
};

type FileDropperProps = Props & {
  showAcceptExtensions?: boolean;
  hidePlaceholder?: boolean;
};
export const FileDropperImage = forwardRef<
  { click: () => void },
  FileDropperProps
>(
  (
    {
      square,
      onChange,
      name,
      accept,
      error,
      validationError,
      isLoading,
      isFinishing,
      disabled,
      placeholder,
      onFileRemoved,
      additionalButton,
      additionalLabel,
      hidePlaceholder,
      showAcceptExtensions = true,
    }: FileDropperProps,
    ref
  ) => {
    const { file, error: uploadError } = useFileDropperContext(name);

    useImperativeHandle(ref, () => ({
      click: () => {
        inputRef.current?.click();
      },
    }));

    const { onDrop, inputRef, launchFileDialog, inputId, progress } =
      useFileDropperHandlers({
        name,
        onChange,
      });

    const optionalProps = getOptionalProps(accept);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      multiple: false,
      disabled,
      ...optionalProps,
    });

    const hasFileError = error || uploadError;
    const displayError = hasFileError || validationError;

    return (
      <>
        {file ? (
          <FileDropperProgress
            file={file}
            name={name}
            progress={progress}
            isFinishing={isFinishing}
            error={uploadError}
            onClearFile={onFileRemoved}
          />
        ) : (
          <>
            <Styled.FileDropperImageField
              {...getRootProps({
                onClick: (e: MouseEvent) => {
                  e.stopPropagation();
                  launchFileDialog();
                },
              })}
              {...optionalProps}
              $activeDrag={isDragActive}
              $isLoading={isLoading}
              $hasError={!!(displayError || validationError)}
            >
              <Styled.FileDropperInput
                id={inputId}
                name={name}
                data-selector={name}
                {...getInputProps()}
                disabled={disabled}
                aria-label="Select a file to upload"
                ref={inputRef}
                onClick={() => {
                  if (hasFileError && inputRef && inputRef.current) {
                    inputRef.current.value = '';
                  }
                }}
                aria-describedby={`file-error-${name}`}
                aria-invalid={!!displayError}
              />
              <Styled.FileDropperImagePlaceholder>
                <IconUpload width={24} height={24} />
                <PStyled marginBottom={spacing4}>{placeholder}</PStyled>
                {!hidePlaceholder && !placeholder && (
                  <PStyled marginBottom={spacing4}>
                    Drag and drop your file here
                  </PStyled>
                )}
                {additionalLabel && (
                  <Styled.FileDropperSecondaryText marginBottom={4}>
                    {additionalLabel}
                  </Styled.FileDropperSecondaryText>
                )}
                {showAcceptExtensions && accept && (
                  <Styled.FileDropperSecondaryText marginBottom={0}>
                    Files supported: {getAcceptExtensions(accept)}
                  </Styled.FileDropperSecondaryText>
                )}
              </Styled.FileDropperImagePlaceholder>

              {additionalButton && (
                <Styled.FileDropperButtons>
                  {additionalButton && (
                    <SecondaryButton
                      type="button"
                      onClick={event => {
                        event.stopPropagation();
                        additionalButton.onClick();
                      }}
                      aria-disabled={isDragActive}
                    >
                      {additionalButton.text}
                    </SecondaryButton>
                  )}
                </Styled.FileDropperButtons>
              )}

              {(displayError || validationError) && (
                <ErrorMessageStyled
                  id={`file-error-${name}`}
                  data-selector={`file-error-${name}`}
                >
                  {displayError}
                </ErrorMessageStyled>
              )}
            </Styled.FileDropperImageField>
          </>
        )}
      </>
    );
  }
);
